import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import Model3D from "../Model3D.jsx";
import { getName } from "../profile.js";
import { t, sampleNames } from "../i18n.js";
import { ensureCtx, out, speak } from "../audio.js";

/* ==================== RACERBILER – top-down oval race =========================
   Radio-car racing seen from above: steer left/right around an oval, grab ⚡
   boosts for speed, and BUMP the opponents — collisions are part of the fun.
   First to LAPS laps wins. Prize by difficulty: let=lille, mellem=mellem,
   svær=GULDPOKAL (the cup goes to real champions only).
================================================================================ */

const W = 360, H = 540;
const CX = W / 2, CY = 264;
const A0 = 127, B0 = 198;            // base midline ellipse (portrait oval)
const LX = 168, LY = 246;            // canvas limits for the auto-fit "zoom"
const CAR_R = 11;

/* ---- selectable tracks: midline radius = ellipse(θ) · fn(θ), auto-scaled
   to fill the canvas. Polar form keeps CPU pursuit, lap counting and wall
   physics working on every shape (radius stays single-valued per angle). */
/* a "notch" pinches the track toward the centre at the top (the heart's dip) */
const notch = (th, depth, width) => {
  let d = th + Math.PI / 2;              // afstand fra topcentrum
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return 1 - depth * Math.exp(-((d / width) ** 2));
};
const TRACKS = {
  oval:  { w: 41, fn: () => 1 },
  heart: { w: 38, fn: (th) => notch(th, 0.34, 0.40) },                       // Hjertebanen: skarp kærv i toppen
  tri:   { w: 39, fn: (th) => 1 + 0.11 * Math.cos(3 * th + Math.PI / 2) },   // tre bløde sving
};
const LAPS = 5;
const JOY = { x: W - 58, y: H - 52, r: 38, knobR: 16, range: 26, grab: 88 };  // on-canvas joystick
const PLAYER_MAX = 175;
const STEER = 3.6;                   // rad/s at full lock
const GRIP = 6;                      // how fast velocity follows the nose
const BOOST_T = 2.2, BOOST_MULT = 1.5;
const CPU_BOOST_MULT = 1.3;          // CPU'er booster svagere end spilleren
const N_BOOSTS = 3, BOOST_RESPAWN = 3.5;
const START_TH = Math.PI / 2;        // start/finish at the bottom of the oval

const DIFFS = {
  let:    { cpuMax: 138, prize: "lille" },
  mellem: { cpuMax: 158, prize: "mellem" },
  svaer:  { cpuMax: 176, prize: "trophy" },   // faster than the player: boost + bump!
};
const CPU_COLORS = ["#C8352E", "#E08A1E", "#2E8B57"];
const YOU_COLOR = "#1B5FBF";
// driver names come from the language's pool (i18n names_race), drawn
// randomly per race; shortName keeps them fitting the standings board
const shortName = (s) => (s.length > 9 ? s.slice(0, 8) + "…" : s);

/* Speakeren er ALTID engelsk mandestemme (samme som Hestevæddeløbet, Daniels
   valg 18/7) og omtaler bilerne som NUMMER + FARVE (spilleren = nr. 1, blå). */
const SPEAK_COLORS = ["blue", "red", "orange", "green"];
const ANN_OFF = "And they're off!";
const ANN_LEAD = [
  "And now it's number {num}, the {color} car, in front!",
  "Number {num} takes the lead! Go {color} car!",
  "Here comes number {num}! The {color} car hits the front!",
  "It's number {num} now, the {color} car, out in front!",
];
const ANN_AWAY = [
  "{Color} has broken away from the field!",
  "Number {num} is pulling away! Can anyone catch the {color} car?",
  "The {color} car is out on its own! What a gap!",
];
const ANN_CATCH = [
  "{Color} is now catching up!",
  "Number {num} is closing in on the leader!",
  "Here comes the {color} car, closing fast!",
];
const ANN_BACK = [
  "{Color} is struggling at the back of the race!",
  "Number {num} is falling behind the field!",
  "The {color} car is losing touch back there!",
];
const ANN_WIN = [
  "And the winner is number {num}, the {color} car!",
  "Number {num} takes it! The {color} car wins the race!",
  "It's the {color} car! Number {num} takes the chequered flag!",
];
const annFmt = (line, car) => {
  const c = SPEAK_COLORS[car.i] || "grey";
  return line.replace("{num}", String(car.i + 1)).replace("{color}", c)
    .replace("{Color}", c[0].toUpperCase() + c.slice(1));
};

// top-down sprites baked from the front-page F1 model (nose points -X);
// pre-tinted per colour because ctx.filter doesn't work on iOS
const SPRITE_KEYS = ["blue", "red", "orange", "green"];
const CAR_SPRITES = {};
if (typeof Image !== "undefined")
  for (const k of SPRITE_KEYS) {
    const img = new Image();
    img.src = `/images/cars/car_${k}.png`;
    CAR_SPRITES[k] = img;
  }

const wrapAng = (a) => { while (a > Math.PI) a -= Math.PI * 2; while (a < -Math.PI) a += Math.PI * 2; return a; };
/* builds the active track's geometry: midline radius, points, tangents */
const buildTrack = (track) => {
  const E = (th) => (A0 * B0) / Math.hypot(B0 * Math.cos(th), A0 * Math.sin(th));
  // auto-fit: the largest scale where midline + width stays inside the canvas
  let s = 10;
  for (let i = 0; i < 128; i++) {
    const th = (i / 128) * Math.PI * 2;
    const R = E(th) * track.fn(th);
    const co = Math.abs(Math.cos(th)), si = Math.abs(Math.sin(th));
    const rhoMax = Math.min(co > 1e-4 ? LX / co : 1e9, si > 1e-4 ? LY / si : 1e9);
    s = Math.min(s, (rhoMax - track.w) / R);
  }
  const trackR = (th) => E(th) * track.fn(th) * s;
  const linePoint = (th, m = 1) => {
    const R = trackR(th) * m;
    return { x: CX + Math.cos(th) * R, y: CY + Math.sin(th) * R };
  };
  const tangent = (th) => {
    const p1 = linePoint(th + 0.03), p0 = linePoint(th - 0.03);
    return Math.atan2(p1.y - p0.y, p1.x - p0.x);
  };
  const angleOf = (x, y) => Math.atan2(y - CY, x - CX);
  return { trackR, linePoint, tangent, angleOf, W2: track.w };
};

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null, engine = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    beep() { const c = ensure(); if (c) tone(440, c.currentTime, 0.18, "square", 0.1); },
    go() { const c = ensure(); if (c) tone(700, c.currentTime, 0.4, "square", 0.12); },
    boost() { const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth"; o.frequency.setValueAtTime(220, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(880, c.currentTime + 0.3);
      g.gain.setValueAtTime(0.1, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.35);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.35); },
    thud() { const c = ensure(); if (c) tone(90, c.currentTime, 0.12, "triangle", 0.14); },
    skid() { const c = ensure(); if (c) tone(160, c.currentTime, 0.2, "sawtooth", 0.06); },
    win() { const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14)); },
    engineStart() {
      const c = ensure(); if (!c || engine) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth"; o.frequency.value = 60;
      g.gain.value = 0.028;
      o.connect(g); g.connect(out()); o.start();
      engine = { o, g };
    },
    engineSpeed(v) { if (engine) engine.o.frequency.setTargetAtTime(55 + v * 0.55, ctx.currentTime, 0.08); },
    engineStop() { if (engine) { try { engine.g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.05); engine.o.stop(ctx.currentTime + 0.3); } catch {} engine = null; } },
  };
}

export default function RaceCars({ onExit }) {
  const hudRef = useRef({ lap: 1, pos: 1, boost: 0 });
  const [phase, setPhase] = useState("setup");   // setup | playing | finished | failed
  const [numCpu, setNumCpu] = useState(3);
  const [diffKey, setDiffKey] = useState("mellem");
  const [trackKey, setTrackKey] = useState("oval");
  const [hud, setHud] = useState({ lap: 1, pos: 1, boost: 0 });
  const [result, setResult] = useState(null);    // { place }
  const [claimed, setClaimed] = useState(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setResult(null);
    setClaimed(null);
    setHud({ lap: 1, pos: 1, boost: 0 });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const audio = audioRef.current;
    const diff = DIFFS[diffKey];
    const { trackR, linePoint, tangent, angleOf, W2 } = buildTrack(TRACKS[trackKey]);

    /* cars: player is index 0, staggered grid just before the line */
    const mkCar = (i, color, name, isYou, spriteKey) => {
      const th = START_TH - 0.10 - i * 0.11;
      const m = 0.90 + (i % 2) * 0.18;
      const p = linePoint(th, m);
      // tangent of increasing theta = race direction (clockwise on screen)
      const heading = tangent(th);
      return {
        i, color, name, isYou, spriteKey,
        x: p.x, y: p.y, vx: 0, vy: 0, heading,
        speed: 0, boostT: 0,
        th, progress: -(0.10 + i * 0.11) / (Math.PI * 2),  // grid offset (in laps): everyone crosses the line at 0
        lap: 1, finished: false,
        lineM: 0.9 + Math.random() * 0.2,  // CPU's personal racing line
        maxNoise: 1, noiseT: 0,
      };
    };
    const cars = [mkCar(0, YOU_COLOR, shortName(getName() || t("you")), true, "blue")];
    const cpuNames = sampleNames("race", numCpu);
    const cpuSprites = ["red", "orange", "green"];
    for (let i = 0; i < numCpu; i++) cars.push(mkCar(i + 1, CPU_COLORS[i], shortName(cpuNames[i]), false, cpuSprites[i]));

    const g = {
      state: "count", t: 0, count: 3,
      steer: 0, down: false, joy: false, finishCount: 0,
      popups: [], smoke: [], over: false,
      boosts: Array.from({ length: N_BOOSTS }, () => spawnBoost()),
      leadI: null, leadAt: 0, lastVariant: -1,     // speaker: føringsskift
      spokeAt: 0, lastComment: "",                 // speaker: farvekommentarer
    };
    function spawnBoost() {
      const th = Math.random() * Math.PI * 2;
      const p = linePoint(th, 0.88 + Math.random() * 0.24);
      return { x: p.x, y: p.y, active: true, respawnT: 0 };
    }

    /* ---- sizing (house pattern) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    // test/tuning hook (only when carnival.debug is set): lets a headless
    // driver read car state and steer — never active for normal players
    if (localStorage.getItem("carnival.debug")) window.__rc = { g, cars, linePoint, wrapAng };
    void trackR;   // (kept in scope for tuning/debug)

    /* ---- speaker: føringsskift + farvekommentarer (afstande i omgangs-brøker;
       samme regler som Hestevæddeløbet — føringsskift har forrang, 6,5 s ro
       mellem farvekommentarer, samme type+bil aldrig to gange i træk) ---- */
    const annTick = () => {
      if (g.state !== "race" || g.over) return;
      const sorted = [...cars].sort((x, y) => y.progress - x.progress);
      const leader = sorted[0], second = sorted[1], last = sorted[sorted.length - 1];
      const now = Date.now();
      if (g.finishCount === 0 && leader.progress > 0.1 && leader.i !== g.leadI) {
        if (now - g.leadAt > 3500) {
          let v;
          do { v = Math.floor(Math.random() * ANN_LEAD.length); } while (v === g.lastVariant);
          g.lastVariant = v;
          g.leadI = leader.i;
          g.leadAt = now; g.spokeAt = now;
          speak(annFmt(ANN_LEAD[v], leader), { interrupt: true });
        }
        return;
      }
      if (now - g.spokeAt < 6500) return;
      const cands = [];
      if (g.finishCount === 0 && second && leader.progress - second.progress >= 0.22 && leader.progress < LAPS - 0.3)
        cands.push(["away", leader, ANN_AWAY]);
      if (g.finishCount === 0 && second && second.progress > 0.2 && leader.progress - second.progress <= 0.06 && leader.progress >= 1)
        cands.push(["catch", second, ANN_CATCH]);
      if (cars.length >= 3 && leader.progress - last.progress >= 0.5 && last.progress < LAPS * 0.8)
        cands.push(["back", last, ANN_BACK]);
      const fresh = cands.filter(([type, p]) => type + p.i !== g.lastComment);
      if (!fresh.length) return;
      const [type, p, pool] = fresh[Math.floor(Math.random() * fresh.length)];
      g.lastComment = type + p.i;
      g.spokeAt = now;
      speak(annFmt(pool[Math.floor(Math.random() * pool.length)], p));
    };
    const annInt = setInterval(annTick, 1000);

    /* ---- input: joystick in the corner = analog steering; a press
       elsewhere still steers by canvas half (and arrow keys on desktop) ---- */
    const canvasPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const cx = (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX) - r.left;
      const cy = (e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY) - r.top;
      return { x: cx * (W / r.width), y: cy * (H / r.height) };
    };
    const applyPointer = (p) => {
      if (g.joy || Math.hypot(p.x - JOY.x, p.y - JOY.y) < JOY.grab) {
        g.joy = true;
        g.steer = Math.max(-1, Math.min(1, (p.x - JOY.x) / JOY.range));
      } else {
        g.steer = p.x < W / 2 ? -1 : 1;
      }
    };
    const pDown = (e) => { e.preventDefault(); audio.unlock(); g.down = true; applyPointer(canvasPos(e)); };
    const pMove = (e) => { if (g.down) applyPointer(canvasPos(e)); };
    const pUp = () => { g.down = false; g.joy = false; g.steer = 0; };
    const keys = { left: false, right: false };
    const kDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keys.left = true;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keys.right = true;
    };
    const kUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keys.left = false;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keys.right = false;
    };
    canvas.addEventListener("mousedown", pDown);
    window.addEventListener("mouseup", pUp);
    canvas.addEventListener("mousemove", pMove);
    canvas.addEventListener("touchstart", pDown, { passive: false });
    canvas.addEventListener("touchmove", pMove, { passive: true });
    canvas.addEventListener("touchend", pUp);
    window.addEventListener("keydown", kDown);
    window.addEventListener("keyup", kUp);

    /* ---- physics helpers ---- */
    const wallClamp = (c) => {
      // polar walls: the midline radius ± half width bounds the car;
      // the radial direction is a good-enough normal for these gentle shapes
      const dx = c.x - CX, dy = c.y - CY;
      const rho = Math.hypot(dx, dy) || 1;
      const th = Math.atan2(dy, dx);
      const R = trackR(th);
      const lo = R - W2 + CAR_R, hi = R + W2 - CAR_R;
      if (rho >= lo && rho <= hi) return;
      const outer = rho > hi;
      const nx = dx / rho, ny = dy / rho;
      const cl = outer ? hi : lo;
      c.x = CX + nx * cl; c.y = CY + ny * cl;
      const mx = outer ? -nx : nx, my = outer ? -ny : ny;   // into the track
      const vin = -(c.vx * mx + c.vy * my);
      if (vin > 0) {
        c.vx += mx * vin * 1.4; c.vy += my * vin * 1.4;     // bounce
        c.speed *= 0.82;                                     // scrape penalty
        if (c.isYou && vin > 60) audio.skid();
      }
    };

    const step = (dt) => {
      g.t += dt;

      if (g.state === "count") {
        const left = 3 - Math.floor(g.t);
        if (left !== g.count) { g.count = left; if (left > 0) audio.beep(); }
        if (g.t >= 3) {
          g.state = "race";
          audio.go(); audio.engineStart();
          speak(ANN_OFF);
          g.popups.push({ x: CX, y: CY - 118, txt: t("rc_go"), c: "#7ee87e", t: 0 });
        }
        return;
      }
      if (g.over) return;

      const player = cars[0];
      const steer = g.steer || (keys.left ? -1 : 0) + (keys.right ? 1 : 0);

      for (const c of cars) {
        const max = c.isYou
          ? PLAYER_MAX * (c.boostT > 0 ? BOOST_MULT : 1)
          : diff.cpuMax * c.maxNoise * (c.boostT > 0 ? CPU_BOOST_MULT : 1);

        if (c.isYou) {
          c.heading += steer * STEER * dt * Math.min(1, c.speed / 60 + 0.35);
        } else {
          // chase a point ahead on the personal racing line
          c.noiseT -= dt;
          if (c.noiseT <= 0) {
            c.noiseT = 1.5 + Math.random();
            c.maxNoise = 0.94 + Math.random() * 0.12;
            // opportunistisk boost-jagt: KUN ind imellem (35 %) sigter
            // CPU'en efter et aktivt boost lige fremme — ellers passer den
            // sin racinglinje (de skal ikke støvsuge banen)
            c.hunt = null;
            if (Math.random() < 0.35) {
              for (const bo of g.boosts) {
                if (!bo.active) continue;
                const dth = wrapAng(Math.atan2(bo.y - CY, bo.x - CX) - c.th);
                if (dth > 0.15 && dth < 0.9) { c.hunt = bo; break; }
              }
            }
          }
          const target = (c.hunt && c.hunt.active) ? c.hunt : linePoint(c.th + 0.5, c.lineM);
          const want = Math.atan2(target.y - c.y, target.x - c.x);
          const d = wrapAng(want - c.heading);
          c.heading += Math.max(-STEER * dt, Math.min(STEER * dt, d * 4 * dt));
        }

        c.boostT = Math.max(0, c.boostT - dt);
        c.speed += (max - c.speed) * Math.min(1, 2.2 * dt);
        // velocity follows the nose (arcade grip with a hint of drift)
        const k = Math.min(1, GRIP * dt);
        c.vx += (Math.cos(c.heading) * c.speed - c.vx) * k;
        c.vy += (Math.sin(c.heading) * c.speed - c.vy) * k;
        c.x += c.vx * dt; c.y += c.vy * dt;
        wallClamp(c);

        // lap/progress bookkeeping via the oval parameter angle
        const th = angleOf(c.x, c.y);
        const d = wrapAng(th - c.th);
        c.th = th;
        c.progress += d / (Math.PI * 2);
        const lap = Math.min(LAPS, Math.floor(c.progress) + 1);
        if (c.isYou && !c.finished && lap > c.lap) {
          g.popups.push({ x: CX, y: CY - 40, txt: t("rc_lapup", { n: lap, max: LAPS }), c: "#ffd75e", t: 0 });
        }
        c.lap = Math.max(c.lap, lap);
        // the race runs until THE PLAYER crosses the line — CPUs that finish
        // first keep driving, and the player's final placement is real
        if (c.progress >= LAPS && !c.finished) {
          c.finished = true;
          g.finishCount += 1;
          c.place = g.finishCount;
          // vinderen annonceres i målsekundet (også hvis en CPU vinder midt i løbet)
          if (g.finishCount === 1)
            speak(annFmt(ANN_WIN[Math.floor(Math.random() * ANN_WIN.length)], c), { interrupt: true });
          if (c.isYou) {
            g.over = true;
            audio.engineStop();
            const place = c.place;
            // vandt spilleren, får speakeren tid til vinder-replikken før kortet
            setTimeout(() => {
              setResult({ place });
              if (place === 1) {
                audio.win();
                if (diff.prize === "trophy") setClaimed(saveTrophy({ game: "Racerbiler" }));
                setPhase("finished");
              } else {
                setPhase("failed");
              }
            }, place === 1 ? 2400 : 1200);
            g.popups.push({ x: c.x, y: c.y - 18, txt: t("rc_finishpop"), c: "#fff", t: 0 });
          } else if (g.finishCount === 1) {
            g.popups.push({ x: c.x, y: c.y - 18, txt: t("rc_finishpop"), c: "#fff", t: 0 });
          }
        }
      }

      // car vs car: equal-mass elastic bump + separation (radio-car fun)
      for (let i = 0; i < cars.length; i++)
        for (let j = i + 1; j < cars.length; j++) {
          const a = cars[i], b = cars[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const d = Math.hypot(dx, dy);
          if (d === 0 || d > CAR_R * 2) continue;
          const nx = dx / d, ny = dy / d;
          const overlap = CAR_R * 2 - d;
          a.x -= nx * overlap / 2; a.y -= ny * overlap / 2;
          b.x += nx * overlap / 2; b.y += ny * overlap / 2;
          const avn = a.vx * nx + a.vy * ny, bvn = b.vx * nx + b.vy * ny;
          if (avn - bvn > 0) {
            const swap = (avn - bvn) * 0.9;
            a.vx -= nx * swap; a.vy -= ny * swap;
            b.vx += nx * swap; b.vy += ny * swap;
            if ((a.isYou || b.isYou) && swap > 45) audio.thud();
          }
        }

      // boosts: ALLE biler kan samle dem op (CPU'er booster svagere og jager
      // dem kun opportunistisk); spilleren får lyd + popup, CPU'er kun aura
      for (const bo of g.boosts) {
        if (!bo.active) {
          bo.respawnT -= dt;
          if (bo.respawnT <= 0) Object.assign(bo, spawnBoost());
          continue;
        }
        for (const c of cars) {
          if (Math.hypot(c.x - bo.x, c.y - bo.y) < CAR_R + 10) {
            bo.active = false; bo.respawnT = BOOST_RESPAWN;
            c.boostT = BOOST_T;
            if (c.isYou) {
              audio.boost();
              g.popups.push({ x: bo.x, y: bo.y - 16, txt: t("rc_boost"), c: "#7ee8ff", t: 0 });
            }
            break;
          }
        }
      }

      // udstødningsrøg: små puster bag bilerne i fart (blålige under boost)
      for (const c of cars) {
        c.smokeT = (c.smokeT || 0) - dt;
        if (c.smokeT <= 0 && c.speed > 90) {
          c.smokeT = 0.09;
          g.smoke.push({
            x: c.x - Math.cos(c.heading) * (CAR_R + 4),
            y: c.y - Math.sin(c.heading) * (CAR_R + 4),
            t: 0, boost: c.boostT > 0,
          });
          if (g.smoke.length > 80) g.smoke.shift();
        }
      }
      g.smoke.forEach((s) => (s.t += dt));
      g.smoke = g.smoke.filter((s) => s.t < 0.55);

      audio.engineSpeed(player.speed * (player.boostT > 0 ? 1.3 : 1));

      // HUD (cheap React sync)
      const rank = [...cars].sort((x, y) => y.progress - x.progress).findIndex((o) => o.isYou) + 1;
      if (rank !== hudRef.current.pos || player.lap !== hudRef.current.lap || (player.boostT > 0) !== hudRef.current.boost > 0) {
        hudRef.current = { lap: player.lap, pos: rank, boost: player.boostT };
        setHud(hudRef.current);
      }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1.1);
    };

    /* ---- drawing ---- */
    const drawCar = (c) => {
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(c.heading);
      if (c.boostT > 0) {                       // elektrisk aura + flamme
        ctx.save();
        ctx.shadowColor = "#4fc3ff"; ctx.shadowBlur = 12;
        ctx.strokeStyle = "rgba(126,210,255,0.7)"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(0, 0, CAR_R + 4, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
        ctx.lineWidth = 1;
        ctx.fillStyle = "rgba(126,232,255,0.85)";
        ctx.beginPath();
        ctx.moveTo(-CAR_R - 9 - Math.random() * 5, 0);
        ctx.lineTo(-CAR_R + 2, -4); ctx.lineTo(-CAR_R + 2, 4);
        ctx.closePath(); ctx.fill();
      }
      const img = CAR_SPRITES[c.spriteKey];
      if (img && img.complete && img.naturalWidth > 0) {
        // baked top-down F1 sprite (nose points -X in the image)
        ctx.rotate(Math.PI);
        const S = CAR_R * 2.9;
        ctx.drawImage(img, -S / 2, -S / 2, S, S);
      } else {
        // fallback while the sprite loads
        ctx.fillStyle = c.color;
        ctx.beginPath();
        ctx.roundRect(-CAR_R, -CAR_R * 0.72, CAR_R * 2, CAR_R * 1.44, 5);
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.35)"; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.85)";                 // windshield
        ctx.fillRect(CAR_R * 0.05, -CAR_R * 0.5, CAR_R * 0.5, CAR_R);
        ctx.fillStyle = "#1b1b1b";                                // wheels
        for (const wx of [-CAR_R * 0.55, CAR_R * 0.45])
          for (const wy of [-CAR_R * 0.86, CAR_R * 0.62])
            ctx.fillRect(wx, wy, CAR_R * 0.55, CAR_R * 0.24);
      }
      ctx.restore();
      if (c.isYou) {                                            // ★ marker
        ctx.font = "bold 11px sans-serif"; ctx.textAlign = "center";
        ctx.fillStyle = "#ffd75e";
        ctx.fillText("★", c.x, c.y - CAR_R - 5);
      }
    };

    const draw = () => {
      // filt-græs med tekstur (Gemini-mockup-looket, uden brands)
      const grad = ctx.createRadialGradient(CX, CY, 60, CX, CY, 320);
      grad.addColorStop(0, "#33813f");
      grad.addColorStop(1, "#1c4f26");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      for (let i = 0; i < 220; i++) {          // deterministisk græs-nister
        const nx = (i * 97) % W, ny = (i * 211) % H;
        ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.06)";
        ctx.fillRect(nx, ny, 2, 2);
      }

      // sandgrave: ÉN organisk klat pr. hjørne (wobbly kant + gradient — ikke
      // overlappende cirkler), lagt dynamisk uden for banen m. luft imellem,
      // så de aldrig støder hårdt op til asfalten, uanset baneform
      const sandBlob = (dirTh, seed) => {
        const br = 26 + (seed % 3) * 5;
        const R = trackR(dirTh) + W2 + br + 20;
        const cx0 = CX + Math.cos(dirTh) * R, cy0 = CY + Math.sin(dirTh) * R;
        ctx.beginPath();
        for (let a = 0; a <= 26; a++) {
          const tt = (a / 26) * Math.PI * 2;
          const wob = 1 + 0.2 * Math.sin(tt * 3 + seed * 2.1) + 0.11 * Math.sin(tt * 5 + seed * 4.7);
          const px = cx0 + Math.cos(tt) * br * wob;
          const py = cy0 + Math.sin(tt) * br * wob * 0.78;
          a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.closePath();
        const sg = ctx.createRadialGradient(cx0 - br * 0.2, cy0 - br * 0.2, 2, cx0, cy0, br * 1.3);
        sg.addColorStop(0, "#ddc99d");
        sg.addColorStop(0.7, "#ccb486");
        sg.addColorStop(1, "#b1976a");
        ctx.fillStyle = sg; ctx.fill();
        ctx.strokeStyle = "rgba(70,55,25,0.20)"; ctx.lineWidth = 2; ctx.stroke();
        ctx.lineWidth = 1;
        for (let i = 0; i < 12; i++) {           // sandkorn
          ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.22)" : "rgba(90,70,35,0.18)";
          ctx.fillRect(
            cx0 + (((i * 37 + seed * 13) % 100) / 100 - 0.5) * br * 1.2,
            cy0 + (((i * 53 + seed * 29) % 100) / 100 - 0.5) * br * 0.9,
            1.6, 1.6
          );
        }
      };
      for (const [dth, seed] of [[-2.32, 1], [-0.72, 2], [2.42, 3]]) sandBlob(dth, seed);

      // dæk-krusseduller i infielden (bag placeringstavlen)
      ctx.strokeStyle = "rgba(0,0,0,0.09)"; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(CX - 34, CY + 96, 26, 0.4, 4.2); ctx.stroke();
      ctx.beginPath(); ctx.arc(CX + 30, CY - 102, 20, 2.2, 5.9); ctx.stroke();
      ctx.lineWidth = 1;

      // asphalt ring: sampled polygon (works for every track shape)
      const SEG = 96;
      const edgePt = (i, off) => {
        const th = (i / SEG) * Math.PI * 2;
        const R = trackR(th) + off;
        return [CX + Math.cos(th) * R, CY + Math.sin(th) * R];
      };
      ctx.beginPath();
      for (let i = 0; i <= SEG; i++) {
        const [x, y] = edgePt(i, W2);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      for (let i = SEG; i >= 0; i--) {
        const [x, y] = edgePt(i, -W2);
        i === SEG ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = "#3c4048"; ctx.fill("evenodd");

      // slidte dækspor langs racinglinjen (statisk pynt, giver brugt asfalt)
      const ghost = (off, alpha, lw, dash) => {
        ctx.setLineDash(dash);
        ctx.strokeStyle = `rgba(15,16,20,${alpha})`; ctx.lineWidth = lw;
        ctx.beginPath();
        for (let i = 0; i <= SEG; i++) {
          const [x, y] = edgePt(i, off);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath(); ctx.stroke();
      };
      ghost(-9, 0.14, 5, [46, 30]);
      ghost(2, 0.18, 6, [64, 22]);
      ghost(9, 0.11, 4, [34, 40]);
      ctx.setLineDash([]); ctx.lineWidth = 1;

      // red/white kerbs on both edges
      for (const off of [W2 - 3, -(W2 - 3)]) {
        for (let i = 0; i < 36; i++) {
          ctx.beginPath();
          ctx.strokeStyle = i % 2 ? "#d84b4b" : "#f2ead2";
          ctx.lineWidth = 5;
          for (let k = 0; k <= 4; k++) {
            const th = ((i + k / 8) / 36) * Math.PI * 2;
            const R = trackR(th) + off;
            const x = CX + Math.cos(th) * R, y = CY + Math.sin(th) * R;
            k === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
      }
      ctx.lineWidth = 1;

      // dashed centre line
      ctx.setLineDash([10, 12]);
      ctx.strokeStyle = "rgba(255,255,255,0.35)"; ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i <= SEG; i++) {
        const [x, y] = edgePt(i, 0);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath(); ctx.stroke();
      ctx.setLineDash([]); ctx.lineWidth = 1;

      // start/finish: checkered strip at the bottom (θ = π/2)
      ctx.save();
      const inY = CY + trackR(START_TH) - W2, outY = CY + trackR(START_TH) + W2;
      for (let r = 0; r < 2; r++)
        for (let i = 0; i < 10; i++) {
          ctx.fillStyle = (i + r) % 2 ? "#111" : "#fff";
          ctx.fillRect(CX - 5 + r * 5, inY + ((outY - inY) / 10) * i, 5, (outY - inY) / 10);
        }
      ctx.restore();

      // boosts: pulserende elektrisk kugle m. håndtegnet lyn (ingen emoji)
      for (const bo of g.boosts) {
        if (!bo.active) continue;
        const pul = 1 + 0.14 * Math.sin(g.t * 5 + bo.x);
        ctx.save();
        ctx.shadowColor = "#4fc3ff"; ctx.shadowBlur = 12;
        const og = ctx.createRadialGradient(bo.x, bo.y, 1, bo.x, bo.y, 10.5 * pul);
        og.addColorStop(0, "rgba(255,255,255,0.95)");
        og.addColorStop(0.45, "rgba(126,210,255,0.8)");
        og.addColorStop(1, "rgba(30,80,140,0.12)");
        ctx.fillStyle = og;
        ctx.beginPath(); ctx.arc(bo.x, bo.y, 10.5 * pul, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        // elektriske gnist-buer, der roterer om kuglen
        ctx.strokeStyle = "rgba(170,228,255,0.85)"; ctx.lineWidth = 1.4;
        for (const ph of [0, 2.6]) {
          const a0 = g.t * 3.2 + ph + bo.y;
          ctx.beginPath(); ctx.arc(bo.x, bo.y, 12 * pul, a0, a0 + 1.1); ctx.stroke();
        }
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(bo.x + 1.5, bo.y - 6);
        ctx.lineTo(bo.x - 3.5, bo.y + 1);
        ctx.lineTo(bo.x - 0.5, bo.y + 1);
        ctx.lineTo(bo.x - 1.5, bo.y + 6);
        ctx.lineTo(bo.x + 3.5, bo.y - 1);
        ctx.lineTo(bo.x + 0.5, bo.y - 1);
        ctx.closePath();
        ctx.fillStyle = "#ffe066"; ctx.fill();
      }

      // udstødningsrøg under bilerne
      for (const s of g.smoke) {
        const a = Math.max(0, 0.26 * (1 - s.t / 0.55));
        ctx.fillStyle = s.boost ? `rgba(140,210,255,${a})` : `rgba(205,205,210,${a})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, 2.5 + s.t * 9, 0, Math.PI * 2); ctx.fill();
      }

      for (const c of cars) drawCar(c);

      // live standings in the infield: "1. Blå ★  2. Rød …", 🏁 when finished
      if (g.state === "race") {
        const order = [...cars].sort((a, b) =>
          a.finished && b.finished ? a.place - b.place
          : a.finished ? -1 : b.finished ? 1
          : b.progress - a.progress);
        const pw = 150, rowH = 20, ph = order.length * rowH + 16;
        ctx.fillStyle = "rgba(10,14,20,0.55)";
        ctx.beginPath(); ctx.roundRect(CX - pw / 2, CY - ph / 2, pw, ph, 10); ctx.fill();
        ctx.font = "bold 13px 'Nunito', system-ui, sans-serif";
        ctx.textAlign = "left"; ctx.textBaseline = "middle";
        order.forEach((c, i) => {
          const y = CY - ph / 2 + 8 + rowH / 2 + i * rowH;
          ctx.beginPath(); ctx.arc(CX - pw / 2 + 16, y, 5.5, 0, Math.PI * 2);
          ctx.fillStyle = c.color; ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.5)"; ctx.stroke();
          ctx.fillStyle = c.isYou ? "#ffd75e" : "#f2ead2";
          ctx.fillText(`${i + 1}. ${c.name}`, CX - pw / 2 + 27, y);
          if (c.finished) {
            // hand-drawn mini checkered flag (emoji rendering varies too much)
            const fx = CX + pw / 2 - 26, fy = y - 6;
            for (let r = 0; r < 3; r++)
              for (let q = 0; q < 5; q++) {
                ctx.fillStyle = (r + q) % 2 ? "#16181c" : "#f2ead2";
                ctx.fillRect(fx + q * 3.6, fy + r * 4, 3.6, 4);
              }
          }
        });
        ctx.textBaseline = "alphabetic";
      }

      // joystick (bottom-right): mørk brønd + kromknop som i mockuppet
      ctx.globalAlpha = g.joy ? 0.95 : 0.7;
      const wellG = ctx.createRadialGradient(JOY.x, JOY.y, 4, JOY.x, JOY.y, JOY.r);
      wellG.addColorStop(0, "#14171c");
      wellG.addColorStop(0.8, "#262b33");
      wellG.addColorStop(1, "#3a4049");
      ctx.beginPath(); ctx.arc(JOY.x, JOY.y, JOY.r, 0, Math.PI * 2);
      ctx.fillStyle = wellG; ctx.fill();
      ctx.strokeStyle = "rgba(220,226,234,0.65)"; ctx.lineWidth = 2; ctx.stroke();
      ctx.font = "bold 11px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(246,238,217,0.75)";
      ctx.fillText("◀", JOY.x - JOY.r + 9, JOY.y + 1);
      ctx.fillText("▶", JOY.x + JOY.r - 9, JOY.y + 1);
      const knobX = JOY.x + (g.joy ? g.steer : 0) * JOY.range;
      const knobG = ctx.createRadialGradient(knobX - 5, JOY.y - 6, 2, knobX, JOY.y, JOY.knobR);
      knobG.addColorStop(0, "#f4f7fa");
      knobG.addColorStop(0.55, "#b9c1cb");
      knobG.addColorStop(1, "#78818e");
      ctx.beginPath(); ctx.arc(knobX, JOY.y, JOY.knobR, 0, Math.PI * 2);
      ctx.fillStyle = knobG; ctx.fill();
      ctx.strokeStyle = "#4c535d"; ctx.stroke();
      ctx.lineWidth = 1; ctx.globalAlpha = 1;
      ctx.textBaseline = "alphabetic";

      // nedtællingstavle: metalplade m. skruer, startlamper og lys-display
      if (g.state === "count") {
        const bx = CX - 84, by = CY - 64, bw = 168, bh = 128;
        const plate = ctx.createLinearGradient(0, by, 0, by + bh);
        plate.addColorStop(0, "#d8dde3"); plate.addColorStop(0.5, "#aab2bc"); plate.addColorStop(1, "#8c95a1");
        ctx.fillStyle = plate;
        ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, 12); ctx.fill();
        ctx.strokeStyle = "#5f6873"; ctx.stroke();
        ctx.fillStyle = "#6d7580";                     // skruer i hjørnerne
        for (const [ox, oy] of [[9, 9], [bw - 9, 9], [9, bh - 9], [bw - 9, bh - 9]]) {
          ctx.beginPath(); ctx.arc(bx + ox, by + oy, 2.6, 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = "#181b20";                     // mørkt indre panel
        ctx.beginPath(); ctx.roundRect(bx + 12, by + 12, bw - 24, bh - 24, 8); ctx.fill();
        for (let i = 0; i < 3; i++) {                  // startlamper (tændes en ad gangen)
          const lit = i < 3 - g.count;
          ctx.save();
          if (lit) { ctx.shadowColor = "#ff4b3e"; ctx.shadowBlur = 12; }
          ctx.beginPath(); ctx.arc(CX - 40 + i * 40, CY - 32, 12, 0, Math.PI * 2);
          ctx.fillStyle = lit ? "#e0392c" : "#3c2a2a"; ctx.fill();
          ctx.restore();
          ctx.strokeStyle = "#565e68";
          ctx.beginPath(); ctx.arc(CX - 40 + i * 40, CY - 32, 12, 0, Math.PI * 2); ctx.stroke();
          if (lit) {                                   // glans i lampen
            ctx.fillStyle = "rgba(255,255,255,0.45)";
            ctx.beginPath(); ctx.ellipse(CX - 44 + i * 40, CY - 37, 4, 2.6, -0.5, 0, Math.PI * 2); ctx.fill();
          }
        }
        ctx.textAlign = "center";
        ctx.save();
        ctx.shadowColor = "#ffb44e"; ctx.shadowBlur = 14;
        ctx.font = "bold 52px 'Alfa Slab One', Georgia, serif";
        ctx.fillStyle = "#ffd77a";
        ctx.fillText(String(Math.max(1, g.count)), CX, CY + 28);
        ctx.restore();
        ctx.font = "bold 12px 'Alfa Slab One', Georgia, serif";
        ctx.fillStyle = "#f2ead2";
        ctx.fillText(t("rc_ready"), CX, CY + 50);
      }

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 26);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 26);
        ctx.globalAlpha = 1;
      }

      // vignette + skrå glas-refleks (miniature-bane under glas)
      const vg = ctx.createRadialGradient(CX, CY, 190, CX, CY, 380);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.26)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
      const sh = ctx.createLinearGradient(0, 0, W, H * 0.8);
      sh.addColorStop(0, "rgba(255,255,255,0.07)");
      sh.addColorStop(0.35, "rgba(255,255,255,0.02)");
      sh.addColorStop(0.5, "rgba(255,255,255,0)");
      ctx.fillStyle = sh;
      ctx.fillRect(0, 0, W, H);
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      audio.engineStop();
      clearInterval(annInt);
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* valgfri */ }
      delete window.__rc;
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", pDown);
      window.removeEventListener("mouseup", pUp);
      canvas.removeEventListener("mousemove", pMove);
      canvas.removeEventListener("touchstart", pDown);
      canvas.removeEventListener("touchmove", pMove);
      canvas.removeEventListener("touchend", pUp);
      window.removeEventListener("keydown", kDown);
      window.removeEventListener("keyup", kUp);
    };
  }, [phase, numCpu, diffKey, trackKey]);

  /* ================= UI ================= */
  const diff = DIFFS[diffKey];

  return (
    <div className={"rc-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_race").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("rc_sign2", { laps: LAPS })}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("rc_lede", { laps: LAPS })}</p>
          <div className="optrow">
            <span className="optlabel">{t("h_opponents")}</span>
            {[1, 2, 3].map((n) => (
              <button key={n} className={"opt" + (numCpu === n ? " sel" : "")} onClick={() => setNumCpu(n)}>{n}</button>
            ))}
          </div>
          <div className="optrow">
            <span className="optlabel">{t("h_difficulty")}</span>
            {Object.keys(DIFFS).map((k) => (
              <button key={k} className={"opt" + (diffKey === k ? " sel" : "")} onClick={() => setDiffKey(k)}>{t("diff_" + k)}</button>
            ))}
          </div>
          <div className="optrow">
            <span className="optlabel">{t("rc_track")}</span>
            {Object.keys(TRACKS).map((k) => (
              <button key={k} className={"opt trk" + (trackKey === k ? " sel" : "")} onClick={() => setTrackKey(k)}>{t("rc_track_" + k)}</button>
            ))}
          </div>
          <ul className="rules">
            <li>{t("rc_rule1")}</li>
            <li>{t("rc_rule2")}</li>
            <li>{t("rc_rule3")}</li>
          </ul>
          <p className="prizenote">{t("rc_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("rc_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("rc_lap", { n: hud.lap, max: LAPS })}</span>
            {hud.boost > 0 && <span className="boostpill">BOOST</span>}
            <span className="last">{t("rc_pos", { n: hud.pos })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && diff.prize !== "trophy" && !claimed && (
        <PrizePicker size={diff.prize} game="Racerbiler" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("rc_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("rc_trophyline")}</p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "finished" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("rc_win")}</div>
            <p className="winline">{t("rc_winline")}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("rc_fail")}</div>
            <p className="winline">{t("rc_failline", { n: result ? result.place : 2 })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.rc-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#141c2b;
  background-image:radial-gradient(circle at 50% -10%, #27364f 0%, #141c2b 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.optrow{display:flex; align-items:center; gap:8px; margin:10px 0; flex-wrap:wrap;}
.optlabel{font-weight:800; font-size:13px; flex-basis:100%;}
.opt{
  flex:1; min-width:0; white-space:nowrap;
  background:#fff; border:2px solid #c9bf9f; color:var(--ink); border-radius:9px;
  font-family:inherit; font-weight:800; font-size:14px; padding:7px 4px; cursor:pointer;
}
.opt.sel{background:var(--red); border-color:#7e1f1a; color:#fff;}
.opt.trk{font-size:12px; padding:7px 2px;}
.rules{list-style:none; margin:8px 0 0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:11.5px; font-weight:800; opacity:.75; line-height:1.4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#2b4a6b; box-shadow:0 4px 0 #16283c; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #16283c;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.boostpill{background:#123a4a; color:#7ee8ff; padding:2px 10px; border-radius:20px; font-size:12px;}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #3c4048; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(8,12,20,.75); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.gold{background:linear-gradient(180deg,#f5c94e,#c8951d); color:#4a3308;}
.ribbon.dark{background:#16283c;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.trophybig{margin:6px 0 2px; display:flex; justify-content:center;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
